/**
 * @file 時間工具函式
 * @description 提供時間格式化相關的工具函式，支援多種格式與時區處理
 */
import { DEF_TIME_ZONE } from 'config/config';

/** 日期格式字串型別 */
export type DateFormatString =
  | 'yyyy/MM'
  | 'yyyy/MM/dd'
  | 'yyyy-MM-dd'
  | 'yyyy/MM/dd HH:mm'
  | 'yyyy/MM/dd HH:mm:ss'
  | 'yyyyMMdd';

/** 時間格式化選項介面 */
export interface FormatDefTimeNewOptions {
  isStart?: boolean | null;
  isUTC?: boolean;
  formatString?: DateFormatString;
}

/**
 * Description 新版時間格式定義
 * @param {string} dateStr - 時間資料。
 * @param {Object} options - 包含 isStart、isUTC、formatString。
 * @param {boolean} options.isStart - 設置為當天開始 true 或結束時間 false，預設 null。
 * @param {boolean} options.isUTC - 設置 UTC+0 時間，預設 false。
 * @param {string} options.formatString - 時間格式處理，預設 yyyy/MM/dd。
 * 有 'yyyy/MM'、'yyyy/MM/dd'、'yyyy-MM-dd'、'yyyy/MM/dd HH:mm'、'yyyy/MM/dd HH:mm:ss'
 * @returns {string} Date string
 */
const formatDefTimeNew = (dateStr: string = '', options: FormatDefTimeNewOptions = {}): string => {
  // config.timeformat.yyyymmdd_hhmmss
  const { isStart = null, isUTC = false, formatString = 'yyyy/MM/dd' } = options;
  if (!dateStr) return '';
  let date = new Date(dateStr);
  date = new Date(date.getTime() + DEF_TIME_ZONE * 3600 * 1000);

  // 根據 isStart 設置為當天開始或結束時間
  if (isStart === true) {
    date.setUTCHours(0, 0, 0, 0); // 設置為當天開始
  } else if (isStart === false) {
    date.setUTCHours(23, 59, 59, 999); // 設置為當天結束
  }

  if (isUTC) {
    const offset = date.getTimezoneOffset() / 60;
    date.setUTCHours(date.getUTCHours() + offset);
  }

  const year = date.getUTCFullYear();
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = date.getUTCDate().toString().padStart(2, '0');
  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  const seconds = date.getUTCSeconds().toString().padStart(2, '0');
  if (formatString === 'yyyy/MM') {
    return `${year}/${month}`;
  } else if (formatString === 'yyyy/MM/dd') {
    return `${year}/${month}/${day}`;
  } else if (formatString === 'yyyy-MM-dd') {
    return `${year}-${month}-${day}`;
  } else if (formatString === 'yyyy/MM/dd HH:mm') {
    return `${year}/${month}/${day} ${hours}:${minutes}`;
  } else if (formatString === 'yyyy/MM/dd HH:mm:ss') {
    return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
  } else if (formatString === 'yyyyMMdd') {
    return `${year}${month}${day}`;
  }
  return `${year}/${month}/${day}`;
};

/**
 * Description 開始結束日期格式
 * @param {string} date - 時間資料。
 * isUTC 固定為 true。formatString 固定為 'yyyy/MM/dd HH:mm:ss'
 * @param {boolean} isStart - 設置為當天開始 true 或結束時間 false。
 * @returns {string} Date string
 */
const formatStartEndDate = (date: string, isStart: boolean): string => {
  return formatDefTimeNew(date, {
    isStart: isStart,
    isUTC: true,
    formatString: 'yyyy/MM/dd HH:mm:ss'
  });
};

/**
 * 選擇時間區間
 * @param dayCounts - 天數偏移量
 * @param startDate - 起始日期 (預設為今天)
 * @returns 計算後的日期
 */
const rangeDate = (dayCounts: number | string, startDate: Date = new Date()): Date =>
  new Date(new Date().setDate(startDate.getDate() + Number(dayCounts)));

export { formatDefTimeNew, formatStartEndDate, rangeDate };
